import { motion } from "framer-motion";

/**
 * Web Applications background — a dense SaaS dashboard with sidebar icons,
 * kanban columns, notification badge, and real-time data widgets.
 * Visually distinct from Landing Pages by showing complex app chrome.
 */

const kanbanCards = [
  { col: 0, cards: [0.7, 0.5, 0.85] },
  { col: 1, cards: [0.6, 0.9] },
  { col: 2, cards: [0.75, 0.5, 0.65, 0.4] },
];

const sidebarIcons = [
  { icon: "grid", active: false },
  { icon: "board", active: true },
  { icon: "chart", active: false },
  { icon: "users", active: false },
  { icon: "gear", active: false },
];

export const CodeEditorBg = () => (
  <div className="absolute inset-0 overflow-hidden">
    <div className="absolute inset-0 flex">
      {/* ── Sidebar ── */}
      <div className="w-[14%] min-w-[22px] border-r border-white/[0.06] flex flex-col items-center pt-3 pb-2 gap-[7px]"
        style={{ backgroundColor: "hsl(0 0% 100% / 0.02)" }}
      >
        {/* App logo */}
        <motion.div
          className="w-[10px] h-[10px] rounded-[3px] mb-2"
          style={{ backgroundColor: "hsl(239 84% 67% / 0.4)" }}
          animate={{ rotate: [0, 90, 90, 0] }}
          transition={{ duration: 6, repeat: Infinity, repeatDelay: 4 }}
        />
        {/* Nav icons */}
        {sidebarIcons.map((item, i) => (
          <motion.div
            key={i}
            className="relative w-[10px] h-[10px] rounded-[2px] flex items-center justify-center"
            style={{
              backgroundColor: item.active ? "hsl(239 84% 67% / 0.25)" : "transparent",
            }}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: item.active ? 1 : 0.4, x: 0 }}
            transition={{ delay: 0.2 + i * 0.08, duration: 0.4 }}
          >
            <div
              className="w-[5px] h-[5px] rounded-[1px]"
              style={{
                backgroundColor: item.active ? "hsl(239 84% 67% / 0.6)" : "hsl(0 0% 100% / 0.15)",
              }}
            />
            {/* Active indicator bar */}
            {item.active && (
              <div className="absolute left-[-3px] top-[2px] bottom-[2px] w-[2px] rounded-full bg-indigo-400/60" />
            )}
          </motion.div>
        ))}
        {/* Bottom avatar */}
        <div className="mt-auto w-[8px] h-[8px] rounded-full bg-emerald-400/30 border border-emerald-400/20" />
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="h-[20px] border-b border-white/[0.06] flex items-center px-3 gap-2 shrink-0"
          style={{ backgroundColor: "hsl(0 0% 100% / 0.01)" }}
        >
          {/* Breadcrumb */}
          <div className="flex items-center gap-[3px]">
            <div className="h-[3px] w-[14px] rounded-full bg-white/10" />
            <span className="text-[4px] text-white/15">/</span>
            <div className="h-[3px] w-[20px] rounded-full bg-white/15" />
          </div>
          {/* Search bar */}
          <div className="ml-auto flex items-center gap-[4px]">
            <div className="h-[10px] w-[40px] rounded-md border border-white/[0.08] bg-white/[0.03] flex items-center px-[4px]">
              <div className="w-[3px] h-[3px] rounded-full border border-white/20" />
              <div className="h-[2px] w-[12px] rounded-full bg-white/8 ml-[3px]" />
            </div>
            {/* Notification bell with badge */}
            <div className="relative">
              <div className="w-[8px] h-[8px] rounded-[2px] bg-white/[0.06] flex items-center justify-center">
                <div className="w-[4px] h-[4px] rounded-[1px] border border-white/15" />
              </div>
              <motion.div
                className="absolute -top-[2px] -right-[2px] w-[4px] h-[4px] rounded-full"
                style={{ backgroundColor: "hsl(0 70% 60% / 0.8)" }}
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </div>
        </div>

        {/* Board header */}
        <div className="px-3 pt-2 pb-1 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-[5px]">
            <div className="h-[5px] w-[28px] rounded-full bg-white/20" />
            <motion.div
              className="h-[8px] px-[4px] rounded-[2px] border border-white/[0.1] flex items-center gap-[2px]"
              style={{ backgroundColor: "hsl(0 0% 100% / 0.04)" }}
            >
              <div className="w-[3px] h-[3px] rounded-full bg-indigo-400/40" />
              <span className="text-[3.5px] text-white/25">Filter</span>
            </motion.div>
          </div>
          <motion.div
            className="h-[8px] px-[5px] rounded-[2px] flex items-center gap-[2px]"
            style={{ backgroundColor: "hsl(239 84% 67% / 0.3)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
          >
            <span className="text-[3.5px] text-white/50">+ New</span>
          </motion.div>
        </div>

        {/* ── Kanban columns ── */}
        <div className="flex-1 flex gap-[5px] px-3 pb-2 min-h-0 overflow-hidden">
          {kanbanCards.map((col, colIdx) => (
            <div key={colIdx} className="flex-1 flex flex-col gap-[4px] min-w-0">
              {/* Column header */}
              <div className="flex items-center gap-[3px] pb-[3px]">
                <div
                  className="w-[4px] h-[4px] rounded-full"
                  style={{
                    backgroundColor: colIdx === 0
                      ? "hsl(45 90% 60% / 0.5)"
                      : colIdx === 1
                        ? "hsl(239 84% 67% / 0.5)"
                        : "hsl(150 60% 50% / 0.5)",
                  }}
                />
                <div className="h-[3px] flex-1 rounded-full bg-white/10" />
                <span className="text-[4px] font-mono text-white/20">{col.cards.length}</span>
              </div>

              {/* Cards */}
              <div className="flex flex-col gap-[3px] flex-1 overflow-hidden">
                {col.cards.map((h, cardIdx) => (
                  <motion.div
                    key={cardIdx}
                    className="rounded-md border border-white/[0.07] p-[4px] flex flex-col gap-[3px] shrink-0"
                    style={{ backgroundColor: "hsl(0 0% 100% / 0.03)" }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 0.4 + colIdx * 0.2 + cardIdx * 0.1,
                      duration: 0.4,
                    }}
                    whileHover={{ scale: 1.02 }}
                  >
                    {/* Card title */}
                    <div className="h-[3px] rounded-full bg-white/12" style={{ width: `${h * 100}%` }} />
                    {/* Card body line */}
                    <div className="h-[2px] rounded-full bg-white/5" style={{ width: `${h * 70}%` }} />
                    {/* Card footer: tag + avatar */}
                    <div className="flex items-center justify-between mt-[1px]">
                      <div
                        className="h-[5px] px-[3px] rounded-[1px] flex items-center"
                        style={{
                          backgroundColor: colIdx === 0
                            ? "hsl(45 90% 60% / 0.12)"
                            : colIdx === 1
                              ? "hsl(239 84% 67% / 0.12)"
                              : "hsl(150 60% 50% / 0.12)",
                        }}
                      >
                        <div className="h-[2px] w-[8px] rounded-full" style={{
                          backgroundColor: colIdx === 0
                            ? "hsl(45 90% 60% / 0.4)"
                            : colIdx === 1
                              ? "hsl(239 84% 67% / 0.4)"
                              : "hsl(150 60% 50% / 0.4)",
                        }} />
                      </div>
                      <div className="w-[5px] h-[5px] rounded-full bg-white/10" />
                    </div>
                  </motion.div>
                ))}

                {/* Drag ghost card (animates in column 1) */}
                {colIdx === 1 && (
                  <motion.div
                    className="rounded-md border border-dashed border-indigo-400/20 p-[4px] shrink-0"
                    style={{ backgroundColor: "hsl(239 84% 67% / 0.04)" }}
                    animate={{ opacity: [0, 0.5, 0.5, 0] }}
                    transition={{ duration: 4, repeat: Infinity, repeatDelay: 6 }}
                  >
                    <div className="h-[3px] w-[60%] rounded-full bg-indigo-400/10" />
                  </motion.div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);
